import { numberWord } from "./trend";
import type { Contact, RunMode, ScenarioSummary } from "./types";

/** A count distribution as bar rows, largest first (ties alphabetical), with each row's share of the total. */
export function distribution(counts: Record<string, number>): { key: string; count: number; share: number }[] {
  const total = Object.values(counts).reduce((a, b) => a + b, 0);
  return Object.entries(counts)
    .map(([key, count]) => ({ key, count, share: total ? count / total : 0 }))
    .sort((a, b) => b.count - a.count || a.key.localeCompare(b.key));
}

/** "ms00_bellamy_howe_prototype" → "Bellamy howe prototype"; used when an id has no title. */
export function humaniseId(id: string): string {
  const s = id.replace(/^ms\d+_/, "").replace(/_/g, " ").trim();
  return s ? s.charAt(0).toUpperCase() + s.slice(1) : id;
}

/**
 * A one-line note on a scenario's length (19 §4.12: setting and length, never the lesson).
 * Uses durationWeeks when the API provides it; otherwise null.
 */
export function scenarioLengthNote(s: ScenarioSummary): string | null {
  const weeks = scenarioWeeksNote(s);
  const time = playTimeNote(s, "full");
  return weeks && time ? `${weeks} · ${time}` : null;
}

/** "eight weeks": the scenario's length in game time; null when the API does not say. */
export function scenarioWeeksNote(s: ScenarioSummary): string | null {
  const w = s.durationWeeks;
  if (!w || w <= 0) return null;
  return w === 1 ? "one week" : `${numberWord(w)} weeks`;
}

/**
 * Quick play's share of full play's playing time (06 Quick play). Measured with the e2e and the bots on ms00:
 * the scripted conversations still come to the door, but routine requests and almost all email do not.
 */
export const QUICK_PLAY_SHARE = 0.4;

/** "about an hour" (full play) or "about 20–25 minutes" (quick play); null when the scenario's length is unknown. */
export function playTimeNote(s: ScenarioSummary, mode: RunMode): string | null {
  const w = s.durationWeeks;
  if (!w || w <= 0) return null;
  // The original ms00 card put eight weeks at about an hour, so roughly 7.5 minutes a week.
  const minutes = w * 7.5;
  if (mode === "quick") {
    const lo = Math.max(5, Math.floor((minutes * QUICK_PLAY_SHARE) / 5) * 5);
    return `about ${lo}–${lo + 5} minutes`;
  }
  return minutes < 45 ? "under an hour" : minutes <= 75 ? "about an hour" : `about ${Math.round(minutes / 30) / 2} hours`;
}

/** Shown wherever a quick-play outcome is shown (06 Quick play, 19). */
export const QUICK_PLAY_OUTCOME_NOTE =
  "Quick play: routine decisions were handled the usual way, so this outcome says less about how you lead than a full run would.";

/** Friendly names for the known feedback questions (06 §6–7); other keys are shown as they are. */
export const FEEDBACK_QUESTIONS: Record<string, string> = {
  uncomfortablyReal: "Felt uncomfortably like their firm",
  understoodHours: "Understood why their hours changed",
  other: "Anything else",
};

export function feedbackText(v: unknown): string {
  if (v === null || v === undefined) return "";
  if (typeof v === "string") return v;
  return JSON.stringify(v);
}

/** The results-email column of the contacts table, in words. */
export function contactEmailStatus(c: Pick<Contact, "resultsRequested" | "emailStatus" | "emailError">): string {
  if (!c.resultsRequested) return "Not asked";
  switch (c.emailStatus) {
    case "sent": return "Asked · sent";
    case "failed": return `Asked · not sent${c.emailError ? ` (${c.emailError})` : ""}`;
    default: return "Asked · waiting to send";
  }
}

const SIGNED_UP = new Intl.DateTimeFormat("en-GB", { day: "numeric", month: "short", year: "numeric" });

/** "9 Oct 2026"; the raw value if it is not a date. */
export function signedUpOn(iso: string): string {
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? iso : SIGNED_UP.format(d);
}

/** Saves text as a file download in the browser. */
export function saveTextFile(text: string, filename: string, type: string): void {
  const url = URL.createObjectURL(new Blob([text], { type }));
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}
